import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import DynamicTextInput from "../../components/Inputs/DynamicTextInput";
import { publicationTypeSchema } from "../../validators/publicationTypeValidators";
import {
  createPublicationType,
  deletePublicationType,
  getPublicationTypes,
  updatePublicationType,
} from "../../services/publicationTypesService";

const PublicationType = () => {
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [filteredPublicationTypes, setFilteredPublicationTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [publicationTypeToEdit, setPublicationTypeToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [publicationTypeToDelete, setPublicationTypeToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadPublicationTypes();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = publicationTypes.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPublicationTypes(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, publicationTypes]);

  const loadPublicationTypes = async () => {
    setLoading(true);
    try {
      const { data } = await getPublicationTypes();
      setPublicationTypes(data.data);
      setFilteredPublicationTypes(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Publication Types"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (publicationTypeToEdit) {
      onUpdatePublicationType(payload, resetForm);
    } else {
      const nonEmptypublicationTypes = payload?.publicationTypes.filter(
        (accreditingBody) => accreditingBody?.name !== ""
      );

      if (nonEmptypublicationTypes.length <= 0) {
        toast.error("Publication Types cannot be empty!");

        return;
      }
      onCreatePublicationTypes(nonEmptypublicationTypes, resetForm);
    }
  };

  const onCreatePublicationTypes = async (publicationTypesArray, resetForm) => {
    setErrs(null);

    try {
      const promises = publicationTypesArray.map(async (accreditingBody) => {
        const payload = { name: accreditingBody.name };

        const { data } = await createPublicationType(payload);

        return data;
      });

      const createdpublicationTypes = await Promise.all(promises);

      setPublicationTypes((prev) => [...prev, ...createdpublicationTypes]);

      toast.success("Publication Type(ies) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { publicationTypes: [{ name: "" }] } });

      await loadPublicationTypes();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error ||
            "Some Publication Types were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setPublicationTypeToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdatePublicationType = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalpublicationTypes = [...publicationTypes];

      const accreditingBodyIndex = originalpublicationTypes.findIndex(
        (accreditingBody) => accreditingBody.id === publicationTypeToEdit.id
      );

      if (accreditingBodyIndex === -1)
        throw new Error("Publication Type not found.");

      const { data } = await updatePublicationType(
        publicationTypeToEdit.id,
        payload
      );

      originalpublicationTypes[accreditingBodyIndex] = data.data;

      setPublicationTypes(originalpublicationTypes);

      setPublicationTypeToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Publication Type updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Publication Type not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setPublicationTypeToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeletePublicationType = async () => {
    const originalpublicationTypes = [...publicationTypes];

    const newpublicationTypes = originalpublicationTypes.filter(
      (accreditingBody) => accreditingBody.id !== publicationTypeToDelete.id
    );

    setPublicationTypes(newpublicationTypes);

    try {
      await deletePublicationType(publicationTypeToDelete?.id);

      toast.success("Publication Type deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Publication Type not deleted, try again later"
        }`
      );

      setPublicationTypes(originalpublicationTypes);
    } finally {
      setIsDeleteModalOpen(false);

      setPublicationTypeToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setPublicationTypeToEdit(null);
    setPublicationTypeToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 500,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Publication Type">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Publication Type">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Publication Types"
        subtitle1="Publications"
        subtitle2="Publication Types"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Publication Types List"
            count={filteredPublicationTypes.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Publication Type"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPublicationTypes.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {publicationTypeToEdit
                    ? "Edit Publication Type"
                    : "Add Publication Type"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {publicationTypeToEdit ? (
              <Formik
                initialValues={{
                  name: publicationTypeToEdit
                    ? publicationTypeToEdit?.name
                    : "",
                }}
                enableReinitialize
                validationSchema={publicationTypeSchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Publication Type"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  publicationTypes: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="publicationTypes">
                      {({ remove, push }) =>
                        (values.publicationTypes || []).map(
                          (accreditingBody, index) => (
                            <DynamicTextInput
                              key={index}
                              index={index}
                              name={`publicationTypes.${index}.name`}
                              placeholder="Enter Publication Type Name"
                              requiredField={true}
                              errors={errors}
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              push={push}
                              remove={remove}
                              pushName="publicationTypes"
                              dynamicFieldMap={{
                                auditCategories: {
                                  name: "",
                                },
                              }}
                            />
                          )
                        )
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeletePublicationType}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default PublicationType;
